import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Users, Trash2, Loader2, UserPlus, Shield } from 'lucide-react'
import { api, type TeamMember } from '@/lib/api'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'manager', label: 'Manager' },
  { value: 'owner', label: 'Owner' },
]

export function TeamPage() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('member')

  const { data, isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => api.getTeamMembers(),
  })

  const addMutation = useMutation({
    mutationFn: () => api.addTeamMember({ email, name: name || undefined, role }),
    onSuccess: () => {
      setEmail('')
      setName('')
      setRole('member')
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      toast.success('Team member added')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.updateTeamMember(id, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      toast.success('Role updated')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      toast.success('Team member removed')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      toast.error('Email is required')
      return
    }
    addMutation.mutate()
  }

  const members = data?.members || []

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/dashboard/settings" className="btn-ghost p-2">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold">Team</h1>
          <p className="text-muted-foreground">
            Assign leads and keep everyone aligned
          </p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Add Team Member
        </h2>
        <form onSubmit={handleAdd} className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <label className="label mb-1.5 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Taylor"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="label mb-1.5 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="taylor@acme.com"
              required
            />
          </div>
          <div className="sm:col-span-1">
            <label className="label mb-1.5 block">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={addMutation.isPending}
              className="btn-primary"
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  Add Member
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Team Members
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            No team members yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-3 text-left text-sm font-semibold">Member</th>
                  <th className="p-3 text-left text-sm font-semibold">Role</th>
                  <th className="p-3 text-right text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member: TeamMember) => (
                  <tr key={member.id} className="border-b border-border/50">
                    <td className="p-3">
                      <div>
                        <p className="font-medium">
                          {member.name || member.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {member.email}
                        </p>
                      </div>
                    </td>
                    <td className="p-3">
                      <select
                        value={member.role}
                        onChange={(e) =>
                          updateMutation.mutate({
                            id: member.id,
                            role: e.target.value,
                          })
                        }
                        disabled={updateMutation.isPending}
                        className={cn('input h-9 text-sm', updateMutation.isPending && 'opacity-50')}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => {
                          if (confirm('Remove this team member?')) {
                            deleteMutation.mutate(member.id)
                          }
                        }}
                        className="btn-ghost btn-sm text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
